// Reusable multi-select. Desktop: dropdown anchored to the trigger.
// Mobile: bottom-sheet with apply/clear footer.
//
// Markup contract:
//   <div data-component="multiselect" data-name="brand" data-label="Marcă"
//        data-placeholder="Orice marcă"></div>
// Selected values are mirrored into hidden inputs so the host <form> submits
// `?brand=BMW&brand=Audi`. Component dispatches `multiselect:change` (bubbles)
// with `{ name, values }` whenever the user commits a new selection.
import { qs, qsa, on, delegate, lockScroll, trapFocus } from '../lib/dom.js';
import { t, sprintf } from '../lib/i18n.js';

const INSTANCE = new WeakMap();

function escapeAttr(s) {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function isMobile() {
  return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
}

export function createMultiselect(root, opts = {}) {
  if (INSTANCE.has(root)) return INSTANCE.get(root);

  const name = opts.name || root.dataset.name || 'value';
  const label = opts.label || root.dataset.label || '';
  let placeholder = opts.placeholder || root.dataset.placeholder || t('ms_placeholder', 'Selectează');
  const summaryWord = opts.summaryWord || root.dataset.summary || 'selectate';
  const maxSelected = (() => {
    const v = opts.maxSelected != null ? opts.maxSelected : root.dataset.msMax;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const selected = new Set();
  let options = Array.isArray(opts.options) ? opts.options.slice() : [];
  let enabled = opts.enabled !== false;
  let onChange = typeof opts.onChange === 'function' ? opts.onChange : null;

  if (!opts.options && root.dataset.options) {
    try { options = JSON.parse(root.dataset.options); } catch { }
  }
  if (root.dataset.selected) {
    root.dataset.selected.split(',').filter(Boolean).forEach((v) => selected.add(v));
  }

  root.classList.add('ap-multiselect');
  root.innerHTML = `
    <button type="button" class="ap-multiselect__trigger" aria-haspopup="listbox" aria-expanded="false">
      <span class="ap-multiselect__value" data-role="ms-value"></span>
      <svg class="ap-multiselect__caret" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
    </button>
    <span class="ap-multiselect__hidden" data-role="ms-hidden"></span>
    <div class="ap-multiselect__panel" data-role="ms-panel" role="dialog" aria-label="${escapeAttr(label || placeholder)}">
      <header class="ap-multiselect__head">
        <span class="ap-multiselect__handle" aria-hidden="true"></span>
        <div class="ap-multiselect__title">${escapeHtml(label || placeholder)}</div>
        <button type="button" class="ap-multiselect__close" data-action="ms-close" aria-label="${escapeAttr(t('ms_close', 'Închide'))}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>
      <div class="ap-multiselect__search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input type="search" placeholder="${escapeAttr(t('ms_search', 'Caută…'))}" data-role="ms-search" autocomplete="off" aria-label="${escapeAttr(t('ms_search_aria', 'Caută în listă'))}">
      </div>
      ${maxSelected ? `<p class="ap-multiselect__hint" data-role="ms-hint">${escapeHtml(sprintf(t('ms_limit_hint', 'Selectează până la %d'), maxSelected))} ${escapeHtml(summaryWord)}.</p>` : ''}
      <ul class="ap-multiselect__list" data-role="ms-list" role="listbox" aria-multiselectable="true"></ul>
      <footer class="ap-multiselect__foot">
        <button type="button" class="ap-btn ap-btn--outline ap-btn--sm" data-action="ms-clear">${escapeHtml(t('ms_clear', 'Resetează'))}</button>
        <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" data-action="ms-apply">${escapeHtml(t('ms_apply', 'Aplică'))} <span data-role="ms-count"></span></button>
      </footer>
    </div>
  `;

  const trigger = qs('.ap-multiselect__trigger', root);
  const valueEl = qs('[data-role="ms-value"]', root);
  const hiddenWrap = qs('[data-role="ms-hidden"]', root);
  const panel = qs('[data-role="ms-panel"]', root);
  const list = qs('[data-role="ms-list"]', root);
  const search = qs('[data-role="ms-search"]', root);
  const countEl = qs('[data-role="ms-count"]', root);

  // Anchor element used for positioning the desktop dropdown. Defaults to the
  // trigger; markup can opt into a wider anchor (e.g. the parent field box)
  // via `data-ms-anchor="<selector>"`. This is how the homepage hero search
  // gets a dropdown that aligns with the full field column instead of the
  // narrow bare-text trigger inside it.
  function resolveAnchor() {
    const sel = root.dataset.msAnchor;
    if (sel) {
      const found = root.closest(sel);
      if (found) return found;
    }
    return trigger;
  }

  let isOpen = false;
  let releaseFocus = null;
  let outsideClickCleanup = null;
  let escCleanup = null;
  let resizeCleanup = null;
  let scrollCleanup = null;
  let vvCleanup = null;          // mobile: visualViewport (keyboard) listeners
  let searchTerm = '';
  let openSnapshot = null;       // for mobile commit-on-apply revert
  let openedAsMobile = false;
  let backdropEl = null;         // real DOM backdrop on mobile (not a ::before)
  // Cache panel's home so we can portal it to <body> on open and restore
  // on close. Portalling guarantees the panel escapes any parent stacking
  // context that would otherwise let other sections paint on top of it.
  const panelHome = panel.parentNode;

  function findLabel(value) {
    const opt = options.find((o) => o.value === value);
    return opt ? opt.label : value;
  }

  function renderTrigger() {
    if (!enabled) {
      trigger.disabled = true;
      valueEl.textContent = placeholder;
      valueEl.classList.add('is-empty');
      root.classList.add('is-disabled');
      root.classList.remove('has-selection');
      return;
    }
    trigger.disabled = false;
    root.classList.remove('is-disabled');

    if (selected.size === 0) {
      valueEl.textContent = placeholder;
      valueEl.classList.add('is-empty');
      root.classList.remove('has-selection');
    } else if (selected.size <= 2) {
      valueEl.textContent = Array.from(selected).map(findLabel).join(', ');
      valueEl.classList.remove('is-empty');
      root.classList.add('has-selection');
    } else {
      valueEl.textContent = `${selected.size} ${summaryWord}`;
      valueEl.classList.remove('is-empty');
      root.classList.add('has-selection');
    }
  }

  function renderHidden() {
    hiddenWrap.innerHTML = Array.from(selected).map((v) =>
      `<input type="hidden" name="${escapeAttr(name)}" value="${escapeAttr(v)}">`
    ).join('');
  }

  function renderList() {
    const term = searchTerm.toLowerCase().trim();
    let filtered = term
      ? options.filter((o) =>
        String(o.label).toLowerCase().includes(term) ||
        String(o.value).toLowerCase().includes(term) ||
        (o.group && String(o.group).toLowerCase().includes(term)))
      : options.slice();

    // Defensive: ensure every currently-selected value is rendered even if
    // it's not in the options list (e.g. a deep-link URL selected a value
    // that the localized brand list doesn't carry under the same name).
    // Without this, the user sees fewer ticked checkboxes than they have
    // selected - which looks like the selection was silently dropped.
    if (selected.size) {
      const present = new Set(filtered.map((o) => o.value));
      const ghosts = [];
      selected.forEach((v) => {
        if (!present.has(v)) {
          ghosts.push({ value: v, label: v });
        }
      });
      if (ghosts.length) {
        filtered = ghosts.concat(filtered);
      }
    }

    const atMax = maxSelected != null && selected.size >= maxSelected;

    if (filtered.length === 0) {
      list.innerHTML = `<li class="ap-multiselect__empty">Nu sunt rezultate</li>`;
    } else {
      let html = '';
      let lastGroup = null;
      filtered.forEach((o) => {
        // Insert a sticky group header whenever the group changes. Items
        // without a `group` field render flat (no header).
        if (o.group && o.group !== lastGroup) {
          html += `<li class="ap-multiselect__group" role="presentation">${escapeHtml(o.group)}</li>`;
          lastGroup = o.group;
        } else if (!o.group) {
          lastGroup = null;
        }
        const isSelected = selected.has(o.value);
        html += `<li class="ap-multiselect__item">
          <label class="ap-multiselect__option">
            <input type="checkbox" value="${escapeAttr(o.value)}"${isSelected ? ' checked' : ''}>
            <span class="ap-multiselect__check" aria-hidden="true"></span>
            <span class="ap-multiselect__label">${escapeHtml(o.label)}</span>
          </label>
        </li>`;
      });
      list.innerHTML = html;
    }
    // Lock unchecked rows at the cap via ONE list-level class (see updateMaxState).
    list.classList.toggle('is-at-max', atMax);
    if (countEl) countEl.textContent = selected.size > 0 ? `(${selected.size})` : '';
  }

  // Lock unchecked checkboxes when the cap is reached, without re-rendering
  // the whole list (preserves scroll + focus during interaction).
  function updateMaxState() {
    if (maxSelected == null) return;
    // Toggle ONE class on the list rather than disabling every unchecked
    // checkbox. Mass-setting `disabled` on ~50 inputs while the list was
    // scrolled made desktop Chrome fail to repaint the scrolled content,
    // leaving the panel blank. CSS (.is-at-max + :has) dims/blocks the
    // unchecked rows, and the change handler's cap guard below is the
    // functional backstop.
    list.classList.toggle('is-at-max', selected.size >= maxSelected);
  }

  function emit() {
    const values = Array.from(selected);
    renderTrigger();
    renderHidden();
    if (onChange) onChange(values);
    root.dispatchEvent(new CustomEvent('multiselect:change', {
      detail: { name, values }, bubbles: true,
    }));
  }

  // Preferred dropdown height. Stable across opens regardless of where on the
  // page the trigger sits - when the viewport can't fit it below the anchor,
  // we keep this height and pin the dropdown to the viewport bottom (which
  // makes it visually overlap the input area), rather than flip above.
  const PREFERRED_HEIGHT = 360;

  function positionDesktopPanel() {
    const anchor = resolveAnchor();
    const r = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 6;
    const margin = 8;

    // Width: match the anchor (so the dropdown lines up with the field box),
    // with a 240 floor so tiny anchors still get a usable dropdown.
    const targetWidth = Math.max(240, Math.round(r.width));
    panel.style.width = targetWidth + 'px';
    panel.style.minWidth = '';

    // Reset before recomputing
    panel.style.left = '';
    panel.style.right = '';
    panel.style.top = '';
    panel.style.bottom = '';

    // Horizontal: align with anchor's left; if that overflows the viewport,
    // align right edges instead. Final clamp keeps the panel inside margins.
    if (r.left + targetWidth > vw - margin) {
      panel.style.right = Math.max(margin, vw - r.right) + 'px';
    } else {
      panel.style.left = Math.max(margin, r.left) + 'px';
    }

    // Vertical: never flip above the trigger. Either:
    //  (a) place the dropdown's top right below the anchor at preferred height; or
    //  (b) when there isn't enough room below, pin the dropdown's bottom to
    //      the viewport (with margin) and let it overlap *over* the anchor.
    // Same height every time you open it from the same trigger.
    // Cap the height and let the panel size to its content up to that cap.
    // IMPORTANT: do NOT clear max-height to "measure the natural height". That
    // unconstrained reflow (a) collapsed the flex list to 0 and (b), when the
    // panel is pinned to the viewport bottom, momentarily blew it up to full
    // content height so its top (and the list inside) shot off the top of the
    // page — leaving a blank/detached dropdown. A plain cap avoids both: with
    // max-height set, the panel grows to its content and then scrolls the list.
    const usable = vh - margin * 2;
    const cap = Math.min(PREFERRED_HEIGHT, usable);
    panel.style.maxHeight = cap + 'px';
    const spaceBelow = vh - r.bottom - margin - gap;

    if (spaceBelow >= cap) {
      // Fits cleanly below
      panel.style.top = (r.bottom + gap) + 'px';
    } else {
      // Cramped - pin bottom to viewport, allow overlap with the input area
      panel.style.bottom = margin + 'px';
    }
  }

  // Mobile sheet: keep the panel above the on-screen keyboard. The sheet is
  // pinned to the layout viewport's bottom (bottom:0), but the virtual keyboard
  // overlays the bottom of the layout viewport WITHOUT shrinking it - iOS never
  // resizes it, and Chrome's default interactive-widget=resizes-visual behaves
  // the same. So without this, the options list and the Apply footer paint
  // *behind* the keyboard: you can type in the search but the filtered results
  // are unreachable. We read the visual viewport to measure the keyboard inset,
  // lift the sheet's bottom above it, and cap the height to the visible region
  // so the list scrolls within reach.
  function adjustSheetForKeyboard() {
    if (!openedAsMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const keyboardInset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    // Ignore sub-keyboard insets (browser-chrome jitter); a real keyboard is tall.
    if (keyboardInset > 80) {
      panel.style.bottom = keyboardInset + 'px';
      panel.style.maxHeight = Math.max(200, Math.round(vv.height) - 12) + 'px';
    } else {
      // Keyboard dismissed - restore the CSS resting sheet (bottom:0, 80dvh).
      panel.style.bottom = '';
      panel.style.maxHeight = '';
    }
  }

  function open() {
    if (isOpen || !enabled) return;
    isOpen = true;
    openSnapshot = new Set(selected);
    openedAsMobile = isMobile();
    root.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    searchTerm = '';
    if (search) search.value = '';
    renderList();

    // Portal the panel out so it escapes any parent stacking context
    // (.ap-search-bar's z-index, transform-based contexts on neighbouring
    // sections, overflow:hidden ancestors, etc.). When the multiselect is
    // inside an OPEN <dialog> (e.g. the catalog filter drawer), we portal
    // into THAT dialog - anything else would render behind it because
    // showModal() puts the dialog in the browser top-layer above body.
    const portalRoot = (() => {
      let node = root.parentElement;
      while (node) {
        if (node.nodeName === 'DIALOG' && node.open) return node;
        node = node.parentElement;
      }
      return document.body;
    })();
    // On mobile we also append a real backdrop element (a sibling of panel)
    // so its dim doesn't paint between the panel's background and its content.
    if (openedAsMobile) {
      backdropEl = document.createElement('div');
      backdropEl.className = 'ap-multiselect__backdrop';
      // Backdrop click closes WITH commit - selections stick (was revert).
      backdropEl.addEventListener('click', () => close(true));
      portalRoot.appendChild(backdropEl);
    }
    if (panel.parentNode !== portalRoot) portalRoot.appendChild(panel);
    panel.classList.add('is-open');
    if (openedAsMobile) panel.classList.add('is-mobile-sheet');
    else panel.classList.remove('is-mobile-sheet');

    if (openedAsMobile) {
      lockScroll(true);
      releaseFocus = trapFocus(panel, { skipInitialFocus: true });
      escCleanup = on(panel, 'keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); close(false); }
      });
      // Track the on-screen keyboard so the sheet (and its results) stay above
      // it - see adjustSheetForKeyboard. visualViewport fires `resize` when the
      // keyboard opens/closes and `scroll` when the viewport pans.
      const vv = window.visualViewport;
      if (vv) {
        adjustSheetForKeyboard();
        const offResize = on(vv, 'resize', adjustSheetForKeyboard);
        const offScroll = on(vv, 'scroll', adjustSheetForKeyboard);
        vvCleanup = () => { offResize(); offScroll(); };
      }
    } else {
      positionDesktopPanel();

      // Defer outside-click so the same click that opened doesn't close.
      setTimeout(() => {
        if (!isOpen) return;
        outsideClickCleanup = on(document, 'mousedown', (e) => {
          if (!root.contains(e.target) && !panel.contains(e.target)) close(true);
        }, { capture: true });
      }, 0);
      escCleanup = on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && isOpen) { e.preventDefault(); close(true); }
      });
      // Reposition on scroll (anywhere on the page) so the dropdown follows
      // its trigger. Skip when scroll happens inside the panel itself.
      let rafId = 0;
      scrollCleanup = on(document, 'scroll', (e) => {
        if (panel.contains(e.target)) return;
        if (rafId) return;
        rafId = requestAnimationFrame(() => { rafId = 0; if (isOpen) positionDesktopPanel(); });
      }, { capture: true, passive: true });
      resizeCleanup = on(window, 'resize', () => {
        // Bail to mobile sheet if the viewport just crossed the breakpoint.
        if (isMobile() !== openedAsMobile) close(true);
        else if (isOpen) positionDesktopPanel();
      });
    }
  }

  function close(commit = true) {
    if (!isOpen) return;
    isOpen = false;

    // Revert-to-snapshot is a legacy iOS-action-sheet pattern. We keep the
    // code path but only the explicit X close button uses it now - every
    // other close-trigger (backdrop tap, Escape, outside click, Apply)
    // commits, so users never lose selections by accident.
    if (!commit && openSnapshot) {
      selected.clear();
      openSnapshot.forEach((v) => selected.add(v));
      renderList();
      renderHidden();
    }
    openSnapshot = null;

    root.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');

    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
    if (outsideClickCleanup) { outsideClickCleanup(); outsideClickCleanup = null; }
    if (escCleanup) { escCleanup(); escCleanup = null; }
    if (resizeCleanup) { resizeCleanup(); resizeCleanup = null; }
    if (scrollCleanup) { scrollCleanup(); scrollCleanup = null; }
    if (vvCleanup) { vvCleanup(); vvCleanup = null; }
    if (openedAsMobile) lockScroll(false);
    openedAsMobile = false;

    // Tear down the portal: clear inline positioning + state classes, then
    // move the panel back to its original parent so a future open starts clean.
    panel.classList.remove('is-open', 'is-mobile-sheet');
    panel.style.cssText = '';
    if (panel.parentNode !== panelHome && panelHome) {
      panelHome.appendChild(panel);
    }
    if (backdropEl) {
      backdropEl.remove();
      backdropEl = null;
    }

    try { trigger.focus({ preventScroll: true }); } catch { }
  }

  on(trigger, 'click', (e) => {
    e.preventDefault();
    if (isOpen) close(true); else open();
  });

  delegate(list, 'change', 'input[type="checkbox"]', (e, cb) => {
    // Defensive cap - disabled handles UI but a programmatic toggle could
    // still fire change. Bounce the change in that case.
    if (cb.checked && maxSelected != null && selected.size >= maxSelected && !selected.has(cb.value)) {
      cb.checked = false;
      return;
    }
    if (cb.checked) selected.add(cb.value); else selected.delete(cb.value);
    if (countEl) countEl.textContent = selected.size > 0 ? `(${selected.size})` : '';
    updateMaxState();
    // Commit on every change (mobile + desktop). Previously mobile deferred
    // to the Apply button, which caused selections to silently disappear
    // if the user closed the sheet via backdrop tap or Escape. Search-form
    // UX expects sticky selections - tick and forget.
    emit();
  });

  on(search, 'input', () => { searchTerm = search.value; renderList(); });
  // Escape commits (was: mobile reverted). Selections stick after Escape.
  on(search, 'keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close(true); } });

  delegate(panel, 'click', '[data-action="ms-apply"]', () => {
    emit();
    close(true);
  });
  delegate(panel, 'click', '[data-action="ms-clear"]', () => {
    selected.clear();
    renderList();
    if (!openedAsMobile) emit();
  });
  delegate(panel, 'click', '[data-action="ms-close"]', () => close(false));

  const api = {
    setOptions(newOptions) {
      // NOTE: previously this method deleted any selected value that wasn't
      // in the new options list. That caused multi-select selections from
      // deep-link URLs to silently shrink whenever the localized options
      // didn't carry the exact same names (different language, different
      // casing, etc.). We now keep every selection - renderList() folds in
      // any "ghost" values as virtual options so they show as ticked.
      options = Array.isArray(newOptions) ? newOptions.slice() : [];
      renderList();
      renderTrigger();
      renderHidden();
      return false;
    },
    setSelected(values) {
      let next = new Set(values || []);
      // Enforce the cap so deep-link URLs with more values than allowed
      // truncate to the first N rather than carrying excess silently.
      if (maxSelected != null && next.size > maxSelected) {
        next = new Set(Array.from(next).slice(0, maxSelected));
      }
      if (next.size === selected.size && [...next].every((v) => selected.has(v))) return false;
      selected.clear();
      next.forEach((v) => selected.add(v));
      renderList();
      renderTrigger();
      renderHidden();
      return true;
    },
    getSelected() { return Array.from(selected); },
    setEnabled(value) {
      const v = !!value;
      if (v === enabled) return;
      enabled = v;
      if (!enabled) {
        if (selected.size) { selected.clear(); renderHidden(); }
        if (isOpen) close(true);
      }
      renderTrigger();
    },
    setOnChange(fn) { onChange = typeof fn === 'function' ? fn : null; },
    setPlaceholder(p) {
      if (typeof p !== 'string' || p === placeholder) return;
      placeholder = p;
      renderTrigger();
    },
    open, close,
    refresh() { renderList(); renderTrigger(); renderHidden(); },
  };

  INSTANCE.set(root, api);

  renderList();
  renderTrigger();
  renderHidden();

  return api;
}

export function getMultiselect(root) {
  return root ? INSTANCE.get(root) || null : null;
}

export function initMultiselects(root = document) {
  qsa('[data-component="multiselect"]', root).forEach((el) => {
    if (el.dataset.bound === '1') return;
    el.dataset.bound = '1';
    createMultiselect(el);
  });
}
